import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'
import { apiClient } from '@/lib/api-client'

export function ProfilePage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [iban, setIban] = useState('')
  const [bankName, setBankName] = useState('')
  const [accountHolder, setAccountHolder] = useState('')
  const [saved, setSaved] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['scout-profile'],
    queryFn: () => apiClient.get<{ profile: any }>('/scouts/me/profile', token!),
    enabled: !!token,
  })

  useEffect(() => {
    if (data?.profile) {
      setIban(data.profile.iban || '')
      setBankName(data.profile.bankName || '')
      setAccountHolder(data.profile.accountHolder || '')
    }
  }, [data])

  const updateMutation = useMutation({
    mutationFn: (payload: { iban: string; bankName: string; accountHolder: string }) =>
      apiClient.put('/scouts/me/profile', payload, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scout-profile'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateMutation.mutate({ iban, bankName, accountHolder })
  }

  if (isLoading) {
    return <div className="p-8 text-center">{t('common.loading')}</div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
          <button
            onClick={() => navigate('/scout/dashboard')}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            &larr; {t('scout.dashboard')}
          </button>
        </div>

        {/* Personal Info (read-only) */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.personalInfo')}</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">{t('auth.firstName')}</span>
              <p className="font-medium text-gray-900">{data?.profile?.firstName}</p>
            </div>
            <div>
              <span className="text-gray-500">{t('auth.lastName')}</span>
              <p className="font-medium text-gray-900">{data?.profile?.lastName}</p>
            </div>
            <div>
              <span className="text-gray-500">{t('auth.email')}</span>
              <p className="font-medium text-gray-900">{data?.profile?.email}</p>
            </div>
            <div>
              <span className="text-gray-500">{t('auth.phone')}</span>
              <p className="font-medium text-gray-900">{data?.profile?.phone || '-'}</p>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.bankDetails')}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('profile.accountHolder')}
              </label>
              <input
                type="text"
                value={accountHolder}
                onChange={(e) => setAccountHolder(e.target.value)}
                placeholder={t('profile.accountHolderPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IBAN
              </label>
              <input
                type="text"
                value={iban}
                onChange={(e) => setIban(e.target.value.toUpperCase().replace(/\s/g, ''))}
                placeholder="DE89 3704 0044 0532 0130 00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('profile.bankName')}
              </label>
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder={t('profile.bankNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {saved && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                {t('common.success')}
              </div>
            )}

            {updateMutation.isError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {t('common.error')}
              </div>
            )}

            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
            >
              {updateMutation.isPending ? t('common.saving') : t('common.save')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
